import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import svgData from './svg.json';
import floorData from './svgHover.json';
import './index.scss';

function ImageSlider({ activeFloor }) {
  const [position, setPosition] = useState(0);
  const [isDragging, setDragging] = useState(false);
  const [initialMouseX, setInitialMouseX] = useState(null);
  const [defaultImage, setDefaultImage] = useState('');
  const [hoveredFloor, setHoveredFloor] = useState(null);
  const preloadedImagesRef = useRef({});
  const animationRef = useRef(null);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [clickedFloor, setClickedFloor] = useState(null);

  const ANCHOR_POINTS = useMemo(() => [7, 17, 27], []);
  const POSITION_PER_IMAGE = 10;

  // Calculates the current image index based on the slider's position
  const calculateImageIndex = useCallback((position) => {
    const offset = Math.floor(position / POSITION_PER_IMAGE);
    const index = 17 - offset;
    return Math.max(1, Math.min(32, index));
  }, []);

  // Finds the nearest anchor point to the current position
  const findNearestAnchor = useCallback(
    (currentPosition) => {
      const currentIndex = calculateImageIndex(currentPosition);
      const nearestAnchor = ANCHOR_POINTS.reduce((prev, curr) =>
        Math.abs(curr - currentIndex) < Math.abs(prev - currentIndex)
          ? curr
          : prev
      );
      return (17 - nearestAnchor) * POSITION_PER_IMAGE;
    },
    [ANCHOR_POINTS, calculateImageIndex]
  );

  // Animates the slider to the target position
  const animateToPosition = useCallback((targetPosition) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const animate = () => {
      setPosition((currentPos) => {
        const diff = targetPosition - currentPos;
        const step = diff > 0 ? 1 : -1;

        if (Math.abs(diff) < 1) {
          cancelAnimationFrame(animationRef.current);
          return targetPosition;
        }

        const newPosition = currentPos + step;
        animationRef.current = requestAnimationFrame(animate);
        return newPosition;
      });
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // hover on
  const handleFloorHover = useCallback((floor, imageIndex) => {
    setHoveredFloor({
      ...floor,
      imageIndex,
    });
  }, []);

  // hover off
  const handleFloorLeave = useCallback(() => {
    setHoveredFloor(null);
  }, []);

  // Close popup when clicking anywhere outside
  const closePopupOnClick = useCallback(() => {
    if (clickedFloor) {
      setClickedFloor(null);
    }
  }, [clickedFloor]);

  useEffect(() => {
    if (clickedFloor) {
      document.addEventListener('click', closePopupOnClick);
      return () => {
        document.removeEventListener('click', closePopupOnClick);
      };
    }
  }, [clickedFloor, closePopupOnClick]);

  useEffect(() => {
    const firstImage = new Image();
    firstImage.src = `/assets/images/img3/17.jpg`;
    firstImage.onload = () => {
      setDefaultImage(firstImage.src);
      preloadedImagesRef.current[17] = firstImage;
    };

    const loadImages = async () => {
      // load anchor images
      const anchorImages = [7, 17, 27];

      for (const index of anchorImages) {
        if (index === 17) continue;
        try {
          const img = new Image();
          img.src = `/assets/images/img3/${index}.jpg`;
          await new Promise((resolve) => {
            img.onload = () => {
              preloadedImagesRef.current[index] = img;
              resolve();
            };
            img.onerror = resolve;
          });
        } catch (error) {
          console.error(`Error loading image ${index}:`, error);
        }
      }

      // load other images
      for (let i = 1; i <= 32; i++) {
        if (anchorImages.includes(i)) continue;
        try {
          const img = new Image();
          img.src = `/assets/images/img3/${i}.jpg`;
          await new Promise((resolve) => {
            img.onload = () => {
              preloadedImagesRef.current[i] = img;
              resolve();
            };
            img.onerror = resolve;
          });
        } catch (error) {
          console.error(`Error loading image ${i}:`, error);
        }
      }
    };

    loadImages();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Handles mouse down event for starting drag
  const handleMouseDown = useCallback(
    (event) => {
      if (event.target.tagName === 'path') return;

      event.preventDefault();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setDragging(true);
      setInitialMouseX(event.clientX - position);
    },
    [position]
  );

  // Handles touch start event for starting drag
  const handleTouchStart = (event) => {
    if (event.target.tagName === 'path') return;

    event.preventDefault();
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setDragging(true);
    const touch = event.touches[0];
    setInitialMouseX(touch.clientX - position);
  };

  // Handles mouse move event for dragging
  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging) return;
      const newPosition = event.clientX - initialMouseX;
      const index = calculateImageIndex(newPosition);
      if (index <= 0 || index > 32) return;
      setPosition(newPosition);
    },
    [isDragging, initialMouseX, calculateImageIndex]
  );

  // Handles touch move event for dragging
  const handleTouchMove = useCallback(
    (event) => {
      if (!isDragging) return;
      event.preventDefault();
      const touch = event.touches[0];
      const newPosition = touch.clientX - initialMouseX;
      const index = calculateImageIndex(newPosition);
      if (index <= 0 || index > 32) return;
      setPosition(newPosition);
    },
    [isDragging, initialMouseX, calculateImageIndex]
  );

  // Handles mouse up event for ending drag
  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setDragging(false);
    const targetPosition = findNearestAnchor(position);
    animateToPosition(targetPosition);
  }, [isDragging, position, findNearestAnchor, animateToPosition]);

  // Handles touch end event for ending drag
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setDragging(false);
    const targetPosition = findNearestAnchor(position);
    animateToPosition(targetPosition);
  }, [isDragging, position, findNearestAnchor, animateToPosition]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Add CSS reset for body and html
  useEffect(() => {
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;

    // Apply reset styles
    document.body.style.cssText =
      'margin: 0; padding: 0; overflow: hidden; width: 100%; height: 100%;';
    document.documentElement.style.cssText =
      'margin: 0; padding: 0; overflow: hidden; width: 100%; height: 100%;';

    return () => {
      // Restore original styles on unmount
      document.body.style.cssText = originalBodyStyle;
      document.documentElement.style.cssText = originalHtmlStyle;
    };
  }, []);

  const currentImage = calculateImageIndex(position);
  const imageSrc =
    preloadedImagesRef.current[currentImage]?.src ||
    defaultImage ||
    '/assets/images/img3/17.jpg';

  // determine svg path
  const getSvgPaths = () => {
    if (currentImage === 7 && svgData[0].paths7) {
      return svgData[0].paths7;
    } else if (currentImage === 17 && svgData[0].paths17) {
      return svgData[0].paths17;
    } else if (currentImage === 27 && svgData[0].paths27) {
      return svgData[0].paths27;
    }
    return [];
  };

  const currentPaths = getSvgPaths();

  // Floor data
  const currentFloorData = floorData[currentImage] || [];

  // Function to toggle popup visibility
  const togglePopup = (floor = null) => {
    setSelectedFloor(floor);
    setPopupVisible(!isPopupVisible);
  };

  // Function to handle path click
  const handlePathClick = (event, floor) => {
    event.stopPropagation();
    setClickedFloor(floor);
  };

  const [lowerFloor, higherFloor] = activeFloor?.split('-') ?? [];

  return (
    <div
      className="image-slider-container"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="image-slider-wrapper">
        <img
          src={imageSrc}
          alt={`Slide ${currentImage}`}
          className="slider-image"
          draggable="false"
        />
        {/* SVG Overlay - same size*/}
        <svg
          width="100%"
          height="100%"
          viewBox={svgData[0].svg.viewBox}
          preserveAspectRatio="xMidYMid slice"
          xmlns={svgData[0].svg.xmlns}
          className="svg-overlay"
        >
          {currentPaths.map((path, index) => {
            if (
              path.floor < parseInt(lowerFloor) ||
              path.floor > parseInt(higherFloor)
            ) {
              return null;
            }
            return (
              <path
                key={index}
                d={path.d}
                fill={path.fill}
                stroke={path.stroke}
                strokeWidth={path['strokeWidth']}
                className="svg-path"
                onMouseEnter={(event) => {
                  if (window.innerWidth > 1024) {
                    handleFloorHover(currentFloorData[index], currentImage);
                    event.target.style.opacity = '0';
                  }
                }}
                onMouseLeave={(event) => {
                  if (window.innerWidth > 1024) {
                    handleFloorLeave();
                    event.target.style.opacity = '1';
                  }
                }}
                onClick={(event) => {
                  if (window.innerWidth > 1024) {
                    handlePathClick(event, currentFloorData[index]);
                  } else {
                    togglePopup(currentFloorData[index]);
                  }
                }}
              />
            );
          })}
        </svg>

        {/* Tooltip detail for desktop */}
        {hoveredFloor && window.innerWidth > 1024 && (
          <div className="tooltip-desktop">
            <h3>{hoveredFloor.name}</h3>
            <h2>{hoveredFloor.nameImg}</h2>
            <img src={hoveredFloor.description} alt="" />
            <p>
              <strong>Area: </strong>
              {hoveredFloor.area}
            </p>
          </div>
        )}

        {/* Full screen image popup for desktop */}
        {clickedFloor && window.innerWidth > 1024 && (
          <div className="fullscreen-popup">
            <img src={clickedFloor.description} alt="" />
          </div>
        )}

        {/* Popup for mobile */}
        {isPopupVisible && selectedFloor && window.innerWidth <= 1024 && (
          <div className="mobile-popup">
            <button onClick={() => togglePopup()} className="close-button">
              &times;
            </button>
            <h3>{selectedFloor.name}</h3>
            <h2>{selectedFloor.nameImg}</h2>
            <img src={selectedFloor.description} alt="" />
            <p>
              <strong>Area: </strong>
              {selectedFloor.area}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageSlider;
